import { useResourcePermissions, type ResourcePermissions } from './useResourcePermissions';

/**
 * Entity permissions type alias
 */
export type EntityPermissions = ResourcePermissions;

/**
 * Brand permissions type alias for backward compatibility
 * @deprecated Use EntityPermissions instead
 */
export type BrandPermissions = EntityPermissions;

/**
 * Hook to get current user's permissions for an entity
 *
 * This is a convenience wrapper around useResourcePermissions that automatically
 * sets the resourceType to 'entity'. It provides the same functionality with a
 * simpler API for the common entity permission use case.
 *
 * Features:
 * - Fetches user's role and permissions for an entity
 * - 5-minute staleTime for stable data
 * - Provides hasPermission helper function
 * - Returns loading and error states
 *
 * @param entityId - ID of the entity (brand, organization, etc.)
 * @returns Entity permissions with helper function and states
 *
 * @example
 * ```tsx
 * const permissions = useEntityPermissions(123);
 *
 * if (permissions.isLoading) return <Spinner />;
 * if (permissions.error) return <Error />;
 *
 * if (permissions.hasPermission('edit_products')) {
 *   return <EditButton />;
 * }
 *
 * if (permissions.hasPermission('manage_members')) {
 *   return <ManageUsersLink />;
 * }
 * ```
 */
export function useEntityPermissions(entityId: number): EntityPermissions {
  return useResourcePermissions('entity', entityId);
}

/**
 * @deprecated Use useEntityPermissions instead
 */
export const useBrandPermissions = useEntityPermissions;
